import type { SessionsListResult } from "../types.ts";
import { parseAgentSessionKey } from "../../../../src/routing/session-key.js";

export function normalizeChatOpenTabs(tabs: string[] | undefined, activeKey: string): string[] {
  const active = activeKey.trim();
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const raw of tabs ?? []) {
    const key = raw.trim();
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    ordered.push(key);
  }
  if (active && !seen.has(active)) {
    ordered.push(active);
  }
  if (ordered.length === 0 && active) {
    return [active];
  }
  return ordered;
}

export function removeChatOpenTab(tabs: string[], key: string, fallbackKey: string): string[] {
  const next = tabs.filter((tab) => tab !== key);
  if (next.length === 0) {
    return [fallbackKey];
  }
  return next;
}

export function resolveChatTabLabel(
  key: string,
  tabIndex: number,
  sessions: SessionsListResult | null,
  /**
   * Sticky fallback label for transient states where the session row is
   * missing (`undefined` during a sessions-list fetch, or present but
   * missing `label`/`displayName`). When a real label is found the
   * caller should pass it on the next render so the chip never flashes
   * between the user-supplied label and the bare session-key fallback.
   *
   * Only used when no `label`/`displayName` is available. Mirrors the
   * `stickyLiveRunHint` behavior on `buildLiveRunSteps` (JAISIU-3153).
   */
  stickyLabel?: string | null,
): string {
  const row = sessions?.sessions?.find((session) => session.key === key);
  const label = row?.label?.trim();
  if (label) {
    return label;
  }
  const displayName = row?.displayName?.trim();
  if (displayName) {
    return displayName;
  }
  // No row or the row is missing both label and displayName (transient
  // sessions-list state). Keep the previous sticky label so the header
  // chip doesn't flicker between the user-supplied label and the bare
  // session-key fallback (JAISIU-3153).
  const sticky = stickyLabel?.trim();
  if (sticky) {
    return sticky;
  }
  if (key === "main") {
    return "Main";
  }
  const parsed = parseAgentSessionKey(key);
  if (parsed?.rest === "main") {
    return "Main";
  }
  if (parsed?.rest?.startsWith("fork:")) {
    return `Chat ${tabIndex + 1}`;
  }
  if (parsed?.rest?.startsWith("session:")) {
    const id = parsed.rest.slice("session:".length);
    return id.length > 10 ? `Session ${id.slice(0, 6)}…` : `Session ${id}`;
  }
  if (key.length <= 20) {
    return key;
  }
  return `${key.slice(0, 8)}…${key.slice(-6)}`;
}

export function nextChatTabLabel(openCount: number): string {
  return `Chat ${openCount + 1}`;
}
